// The /piano-drills transport: a lookahead Web Audio scheduler (same
// setInterval + schedule-ahead pattern the synth features use) that loops the
// drill, plays the metronome and — in guide mode — the chords themselves, and
// in score mode judges incoming notes against the expected events.

import { useCallback, useEffect, useRef, useState } from "react";
import { matchNote, stepMs } from "../../lib/pianoDrills";
import { audioNow, ensureAudio, playClickAt, playToneAt } from "../../lib/pianoDrillsAudio";

const LOOKAHEAD_SEC = 0.12;
const TICK_MS = 25;
// How many steps after an event's beat we wait before calling it a miss —
// comfortably past the matching tolerance at any supported tempo.
const FINALIZE_STEPS = 2;

export const useDrillTransport = ({ drill, bpm, mode, metronome }) => {
  const [playing, setPlaying] = useState(false);
  const [currentAbsStep, setCurrentAbsStep] = useState(-1);
  const [score, setScore] = useState({ hits: 0, misses: 0, extras: 0 });
  // Rolling per-event feedback for the current/most recent loop:
  // { [eventIndex]: "hit" | "miss" }.
  const [statuses, setStatuses] = useState({});

  const runRef = useRef({
    timer: null,
    timeouts: [],
    nextStep: 0, // monotonic step counter across loops
    nextTime: 0, // audio-clock time of nextStep
    loopStart: 0, // audio-clock time of the current loop's step 0
    eventHits: {}, // eventIndex -> Set of midis received this loop
  });
  const playingRef = useRef(false);

  // Live refs so the scheduler and note matcher always see current props
  // without re-binding timers.
  const drillRef = useRef(drill);
  drillRef.current = drill;
  const bpmRef = useRef(bpm);
  bpmRef.current = bpm;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const metronomeRef = useRef(metronome);
  metronomeRef.current = metronome;

  // Mark events as missed once their matching window is safely past. Runs on
  // the UI timeout for the step FINALIZE_STEPS after the event's beat.
  const finalizeStep = useCallback((monotonicStep) => {
    const d = drillRef.current;
    const run = runRef.current;
    const target = monotonicStep - FINALIZE_STEPS;
    if (!d || !d.totalSteps || target < 0) return;
    const absStep = target % d.totalSteps;
    d.events.forEach((event, index) => {
      if (event.absStep !== absStep) return;
      const got = run.eventHits[index];
      const complete = got && got.size >= event.midis.length;
      if (!complete && modeRef.current === "score" && playingRef.current) {
        setStatuses((st) => ({ ...st, [index]: "miss" }));
        setScore((s) => ({ ...s, misses: s.misses + 1 }));
      }
      delete run.eventHits[index];
    });
  }, []);

  const tick = useCallback(() => {
    const d = drillRef.current;
    const run = runRef.current;
    if (!d || !d.totalSteps) return;
    const stepSec = stepMs(bpmRef.current) / 1000;
    while (run.nextTime < audioNow() + LOOKAHEAD_SEC) {
      const monotonicStep = run.nextStep;
      const absStep = monotonicStep % d.totalSteps;
      const time = run.nextTime;
      if (absStep === 0) run.loopStart = time;
      if (metronomeRef.current && absStep % 4 === 0) {
        playClickAt(time, absStep % 16 === 0);
      }
      if (modeRef.current === "guide") {
        d.events.forEach((event) => {
          if (event.absStep !== absStep) return;
          const durMs = event.durSteps * stepMs(bpmRef.current) * 0.95;
          event.midis.forEach((midi) => playToneAt(midi, time, { durMs, gain: 0.11 }));
        });
      }
      const delay = Math.max(0, (time - audioNow()) * 1000);
      const id = setTimeout(() => {
        setCurrentAbsStep(absStep);
        finalizeStep(monotonicStep);
      }, delay);
      run.timeouts.push(id);
      if (run.timeouts.length > 128) run.timeouts.splice(0, 64);
      run.nextStep = monotonicStep + 1;
      run.nextTime = time + stepSec;
    }
  }, [finalizeStep]);

  const stop = useCallback(() => {
    const run = runRef.current;
    if (run.timer) clearInterval(run.timer);
    run.timer = null;
    run.timeouts.forEach(clearTimeout);
    run.timeouts = [];
    run.eventHits = {};
    playingRef.current = false;
    setPlaying(false);
    setCurrentAbsStep(-1);
  }, []);

  const start = useCallback(() => {
    const d = drillRef.current;
    if (!d || d.error || !d.events.length) return;
    if (!ensureAudio()) return;
    const run = runRef.current;
    const t0 = audioNow() + 0.15;
    run.nextStep = 0;
    run.nextTime = t0;
    run.loopStart = t0;
    run.eventHits = {};
    setStatuses({});
    setScore({ hits: 0, misses: 0, extras: 0 });
    playingRef.current = true;
    setPlaying(true);
    run.timer = setInterval(tick, TICK_MS);
    tick();
  }, [tick]);

  const toggle = useCallback(() => {
    if (playingRef.current) stop();
    else start();
  }, [start, stop]);

  // Feed every incoming note (MIDI, on-screen, qwerty) here; only score mode
  // during playback cares.
  const registerNoteOn = useCallback((midi) => {
    if (!playingRef.current || modeRef.current !== "score") return;
    const d = drillRef.current;
    const run = runRef.current;
    if (!d || !d.events.length) return;
    const totalMs = d.totalSteps * stepMs(bpmRef.current);
    let posMs = ((audioNow() - run.loopStart) * 1000) % totalMs;
    if (posMs < 0) posMs += totalMs;
    const match = matchNote(d, bpmRef.current, posMs, midi);
    if (!match) {
      setScore((s) => ({ ...s, extras: s.extras + 1 }));
      return;
    }
    const event = d.events[match.index];
    const got = run.eventHits[match.index] || (run.eventHits[match.index] = new Set());
    if (got.has(midi)) return;
    got.add(midi);
    if (got.size >= event.midis.length) {
      setStatuses((st) => ({ ...st, [match.index]: "hit" }));
      setScore((s) => ({ ...s, hits: s.hits + 1 }));
    }
  }, []);

  const resetScore = useCallback(() => {
    runRef.current.eventHits = {};
    setStatuses({});
    setScore({ hits: 0, misses: 0, extras: 0 });
  }, []);

  // A different drill or tempo invalidates everything scheduled — stop.
  useEffect(() => {
    if (playingRef.current) stop();
  }, [drill, bpm, stop]);

  useEffect(() => stop, [stop]);

  return { playing, toggle, stop, currentAbsStep, score, statuses, registerNoteOn, resetScore };
};
